import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getAgents } from '../../services/agentService';
import { getPersonas } from '../../services/personaService';
import { getKnowledgeBases } from '../../services/knowledgeBaseService';
import { getPrompts } from '../../services/promptService';
import { faEnvelope, faObjectUngroup, faUser } from '@fortawesome/free-regular-svg-icons';
import { faCircleStop, faCode, faDiagramPredecessor, faLandmark, faPaperPlane, faPlay, faPlayCircle, faPlug, faProjectDiagram, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getModels } from '../../services/modelHubService';
import PythonCodeEditor from './PythonCodeEditor';
import { Button } from '../../styles/PageStyles';
import Modal from '../modals/Modal';
import { evaluateNodeExpression, getNodesForPipeline, getWorkStreams } from '../../services/pipelineService';
import { getAllEntitiesByFilter } from '../../services/entityService';

const PanelContainer = styled.div`
  width: 100%;
  background: white;
  border-left: 1px solid #e0e0e0;
  padding: 1rem;
  overflow-y: auto;
`;

const PanelTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const NoSelection = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  text-align: center;
  margin-top: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  margin-bottom: 0.3rem;
  color: var(--text-secondary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const NodeTypeIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.background || '#f0f0f0'};
  color: ${props => props.color || 'var(--text-primary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-right: 1rem;
`;

const NodeHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const NodeInfo = styled.div`
  flex: 1;
`;


const NodeName = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const SectionTitle = styled.h4`
  font-size: 0.9rem;
  margin: 1.5rem 0 0.8rem;
  color: var(--text-secondary);
  padding-bottom: 0.3rem;
  border-bottom: 1px solid #f5f5f5;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Tag = styled.div`
  background: #f0f0f0;
  border-radius: 4px;
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const AddTagButton = styled.button`
  background: transparent;
  border: 1px dashed #ccc;
  border-radius: 4px;
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const RemoveTag = styled.span`
  cursor: pointer;
  font-size: 0.7rem;
  
  &:hover {
    color: #ff3b30;
  }
`;

const PipelineSection = styled.div`
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
`;

const PropertiesPanel = ({
  selectedNode,
  updateNodeProperties,
  pipelineDescription,
  setPipelineDescription,
  selectedPipeline,
  setSelectedPipeline,
  selectedEdge,
  updateEdgeProperties
}) => {

  const [agentList, setAgentList] = useState();
  const [promptList, setPromptList] = useState();
  const [modelList, setModelList] = useState();
  const [personaList, setPersonaList] = useState();
  const [knowledgeBaseList, setKnowledgeBaseList] = useState([]);
  const [entityList, setEntityList] = useState([]);
  const [workStreamList, setWorkStreamList] = useState([]);
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [workflowVarsMode, setWorkflowVarsMode] = useState('manual'); // 'manual' or 'lastRun'
  const [workflowVars, setWorkflowVars] = useState({});
  const [evalResult, setEvalResult] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [lastRunVars, setLastRunVars] = useState(null);
  const [localCodes, setLocalCodes] = useState([]);
  useEffect(() => {
    if (selectedEdge) {
      setLocalCodes(selectedEdge.data?.conditions || []);
    }
  }, [selectedEdge]);

  useEffect(() => {
    const fetchData = async () => {
      let query = `skip=0&limit=200`;
      try {
        // Call all APIs concurrently
        const [agents, personas, knowledgeBases, prompt, models, workStreams] = await Promise.all([
          getAgents({ status: "ACTIVE" }, {}, query),
          getPersonas(null, null, query),
          getKnowledgeBases(null, null, query),
          getPrompts(null, null, query),
          getModels(null, null, query),
          getWorkStreams(null, null, query)
        ]);

        // Fetch entities separately
        const entitiesResponse = await getAllEntitiesByFilter({ type: "entity" }, {}, query);

        // Update state with the fetched data
        setAgentList(agents || []);
        setPersonaList(personas || []);
        setKnowledgeBaseList(knowledgeBases || []);
        setPromptList(prompt || []);
        setModelList(models || []);
        setEntityList(entitiesResponse || []);
        console.log("Work streams fetched:", workStreams);
        setWorkStreamList(workStreams || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);
  if (selectedEdge) {

    const conditions = selectedEdge.data?.conditions || [];


    const handleConditionChange = (idx, value) => {
      const newLocalCodes = [...localCodes];
      newLocalCodes[idx].condition = value;
      setLocalCodes(newLocalCodes);
      // Also update the edge properties in parent
      const newConditions = [...localCodes];
      newConditions[idx].condition = value;
      updateEdgeProperties(selectedEdge.id, { conditions: newConditions });
    };

    const handleLanguageChange = (idx, newLang) => {
      console.log(idx, newLang)
      setLocalCodes(prev => {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], lang: newLang };
        return updated;
      });
    };

    const handleAddCondition = () => {
      const currentConditions = Array.isArray(conditions) ? conditions : [];
      const newConditions = [...currentConditions, {lang:'', condition:''}];
      updateEdgeProperties(selectedEdge.id, { conditions: newConditions });
    };

    const handleRemoveCondition = (idx) => {
      const newConditions = conditions.filter((_, i) => i !== idx);
      updateEdgeProperties(selectedEdge.id, { conditions: newConditions });
    };



    return (
      <PanelContainer>
        <PanelTitle>Edge Properties</PanelTitle>
        <SectionTitle>Conditions</SectionTitle>
        {localCodes.map((localCode, idx) => (
          <FormGroup key={idx}>
            <div style={{ display: 'flex', flexDirection: 'column'}}>
              <Label>Condition {idx + 1}</Label>
              <Select value={localCode.lang}
                onChange={(e) => handleLanguageChange(idx, e.target.value)} style={{ margin: '6px 0' }}>
                <option value="">Select language</option>
                <option value="python">Python</option>
                <option value="eng">English</option>

              </Select>
            </div>
            
            <PythonCodeEditor
              value={localCode.condition}
              lang={localCode.lang}
              onChange={val => handleConditionChange(idx, val)}
              placeholder={
                localCode?.lang == 'eng'
                  ? 'Enter contextual code'
                  : 'Enter condition code'
              }
            />
            <button
              style={{
                marginTop: '0.3rem',
                background: '#ffe0e0',
                color: '#b00020',
                border: 'none',
                borderRadius: '4px',
                padding: '0.2rem 0.7rem',
                cursor: 'pointer'
              }}
              onClick={() => handleRemoveCondition(idx)}
            >
              Remove
            </button>
          </FormGroup>
        ))}
        <button
          style={{
            background: '#e0f7fa',
            color: '#00796b',
            border: 'none',
            borderRadius: '4px',
            padding: '0.4rem 1rem',
            cursor: 'pointer'
          }}
          onClick={handleAddCondition}
          type="button"
        >
          + Add Condition
        </button>
      </PanelContainer>
    );
  }

  if (!selectedNode) {
    return (
      <PanelContainer>
        <PanelTitle>Properties</PanelTitle>

        <PipelineSection>
          <SectionTitle>Pipeline Properties</SectionTitle>

          <FormGroup>
            <Label htmlFor="pipeline-description">Description</Label>
            <Textarea
              id="pipeline-description"
              value={selectedPipeline?.description || ''}
              onChange={(e) => {
                // Ensure we're creating a new object with all existing properties
                const updatedPipeline = { 
                  ...selectedPipeline, 
                  description: e.target.value,
                  // Ensure other fields exist with default values if they don't already
                  value_stream: selectedPipeline?.value_stream || '',
                  entity_name: selectedPipeline?.entity_name || '',
                  associated_entities: selectedPipeline?.associated_entities || [],
                  knowledgebase: selectedPipeline?.knowledgebase || ''
                };
                
                // Convert any string entries in associated_entities to objects with entity_name
                if (updatedPipeline.associated_entities.length > 0) {
                  updatedPipeline.associated_entities = updatedPipeline.associated_entities.map(entity => {
                    if (typeof entity === 'string') {
                      return { entity_name: entity };
                    }
                    // If it has a name property but not entity_name, convert it
                    if (entity.name && !entity.entity_name) {
                      return { entity_name: entity.name };
                    }
                    return entity;
                  });
                }
                
                setSelectedPipeline(updatedPipeline);
              }}
              placeholder="Describe the purpose of this pipeline"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="value-stream">Value Stream</Label>
            <Select
              id="value-stream"
              value={selectedPipeline?.value_stream || ''}
              onChange={(e) => {
                const updatedPipeline = { 
                  ...selectedPipeline, 
                  value_stream: e.target.value,
                  // Ensure other fields exist
                  description: selectedPipeline?.description || '',
                  entity_name: selectedPipeline?.entity_name || '',
                  associated_entities: selectedPipeline?.associated_entities || [],
                  knowledgebase: selectedPipeline?.knowledgebase || ''
                };
                
                // Convert any string entries in associated_entities to objects with entity_name
                if (updatedPipeline.associated_entities.length > 0) {
                  updatedPipeline.associated_entities = updatedPipeline.associated_entities.map(entity => {
                    if (typeof entity === 'string') {
                      return { entity_name: entity };
                    }
                    // If it has a name property but not entity_name, convert it
                    if (entity.name && !entity.entity_name) {
                      return { entity_name: entity.name };
                    }
                    return entity;
                  });
                }
                
                setSelectedPipeline(updatedPipeline);
              }}
            >
              <option value="">Select Work Stream</option>
              {workStreamList && workStreamList.length > 0 ? (
                workStreamList.map((stream, index) => (
                  <option key={index} value={stream.name}>{stream.name}</option>
                ))
              ) : (
                <>
                  <option value="New Business">New Business</option>
                  <option value="Claims Processing">Claims Processing</option>
                  <option value="Servicing">Servicing</option>
                  <option value="Fraud waste and abuse">Fraud waste and abuse</option>
                </>
              )}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="entity-name">Entity Name</Label>
            <Select
              id="entity-name"
              value={selectedPipeline?.entity_name || ''}
              onChange={(e) => {
                const updatedPipeline = { 
                  ...selectedPipeline, 
                  entity_name: e.target.value,
                  // Ensure other fields exist
                  description: selectedPipeline?.description || '',
                  value_stream: selectedPipeline?.value_stream || '',
                  associated_entities: selectedPipeline?.associated_entities || [],
                  knowledgebase: selectedPipeline?.knowledgebase || ''
                };
                
                // Convert any string entries in associated_entities to objects with entity_name
                if (updatedPipeline.associated_entities.length > 0) {
                  updatedPipeline.associated_entities = updatedPipeline.associated_entities.map(entity => {
                    if (typeof entity === 'string') {
                      return { entity_name: entity };
                    }
                    // If it has a name property but not entity_name, convert it
                    if (entity.name && !entity.entity_name) {
                      return { entity_name: entity.name };
                    }
                    return entity;
                  });
                }
                
                setSelectedPipeline(updatedPipeline);
              }}
            >
              <option value="">Select Entity</option>
              {entityList.map((entity, index) => (
                <option key={index} value={entity.name}>{entity.name}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Associated Entities</Label>
            <div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                <Select
                  id="entity-select"
                  style={{ flex: 1 }}
                >
                  <option value="">Select Entity to Add</option>
                  {entityList.filter(entity => {
                    // Filter out entities that are already in associated_entities
                    const associated = selectedPipeline?.associated_entities || [];
                    return !associated.some(item => 
                      (typeof item === 'object' && item?.entity_name === entity.name)
                    );
                  }).map((entity, index) => (
                    <option key={index} value={entity.name}>{entity.name}</option>
                  ))}
                </Select>
                <button
                  type="button"
                  style={{
                    background: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px'
                  }}
                  onClick={() => {
                    const selectElement = document.getElementById('entity-select');
                    if (!selectElement || !selectElement.value) return;
                    
                    const entityName = selectElement.value;
                    const currentEntities = selectedPipeline?.associated_entities || [];
                    
                    // Create entity object with entity_name property
                    const entityToAdd = { entity_name: entityName };
                    
                    // Prevent duplicate entities by checking entity_name
                    if (currentEntities.some(item => 
                      (typeof item === 'object' && item?.entity_name === entityName)
                    )) return;
                    
                    const updatedPipeline = {
                      ...selectedPipeline,
                      associated_entities: [...currentEntities, entityToAdd],
                      // Ensure other fields exist
                      description: selectedPipeline?.description || '',
                      value_stream: selectedPipeline?.value_stream || '',
                      entity_name: selectedPipeline?.entity_name || '',
                      knowledgebase: selectedPipeline?.knowledgebase || ''
                    };
                    setSelectedPipeline(updatedPipeline);
                    
                    // Reset the select to default option after adding
                    selectElement.value = '';
                  }}
                >
                  + Add Entity
                </button>
              </div>


              
              <TagsContainer>
                {(selectedPipeline?.associated_entities || []).map((entity, index) => {
                  // Get entity name from the entity_name property
                  const entityName = entity.entity_name || 'Unknown Entity';
                  
                  return (
                    <Tag key={index}>
                      {entityName}
                      <RemoveTag onClick={() => {
                        const newEntities = [...(selectedPipeline?.associated_entities || [])];
                        newEntities.splice(index, 1);
                        const updatedPipeline = { 
                          ...selectedPipeline, 
                          associated_entities: newEntities,
                          // Ensure other fields exist
                          description: selectedPipeline?.description || '',
                          value_stream: selectedPipeline?.value_stream || '',
                          entity_name: selectedPipeline?.entity_name || '',
                          knowledgebase: selectedPipeline?.knowledgebase || ''
                        };
                        setSelectedPipeline(updatedPipeline);
                      }}>✕</RemoveTag>
                    </Tag>
                  );
                })}
                {(selectedPipeline?.associated_entities || []).length === 0 && (
                  <div style={{ color: '#888', fontSize: '0.9rem' }}>No associated entities selected</div>
                )}
              </TagsContainer>
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="knowledgebase">Knowledge Base</Label>
            <Select
              id="knowledgebase"
              value={selectedPipeline?.knowledgebase || ''}
              onChange={(e) => {
                const updatedPipeline = { 
                  ...selectedPipeline, 
                  knowledgebase: e.target.value,
                  // Ensure other fields exist
                  description: selectedPipeline?.description || '',
                  value_stream: selectedPipeline?.value_stream || '',
                  entity_name: selectedPipeline?.entity_name || '',
                  associated_entities: selectedPipeline?.associated_entities || []
                };
                
                // Convert any string entries in associated_entities to objects with entity_name
                if (updatedPipeline.associated_entities.length > 0) {
                  updatedPipeline.associated_entities = updatedPipeline.associated_entities.map(entity => {
                    if (typeof entity === 'string') {
                      return { entity_name: entity };
                    }
                    // If it has a name property but not entity_name, convert it
                    if (entity.name && !entity.entity_name) {
                      return { entity_name: entity.name };
                    }
                    return entity;
                  });
                }
                
                setSelectedPipeline(updatedPipeline);
              }}
            >
              <option value="">Select Knowledge Base</option>
              {knowledgeBaseList.map((kb, index) => (
                <option key={index} value={kb.code}>{kb.name}</option>
              ))}
            </Select>
          </FormGroup>
        </PipelineSection>

        {/* <NoSelection>
          Select a node to edit its properties
        </NoSelection> */}
      </PanelContainer>
    );
  }

  const getNodeTypeInfo = (type) => {
    switch (type) {
      case 'triggerNode':
        return {
          label: 'Trigger',
          icon: faPlayCircle,
          background: '#FFF4CC',
          color: '#E6C200'
        };
      case 'outputNode':
        return {
          label: 'Output',
          icon: faCircleStop,
          background: '#FFE5CC',
          color: '#FF9900'
        };
      case 'agentNode':
        return {
          label: 'Agent',
          icon: faUser,
          background: '#CCF2FF',
          color: '#0099CC'
        };
      case 'dataSourceNode':
        return {
          label: 'Data Source',
          icon: faDiagramPredecessor,
          background: '#D9F9F2',
          color: '#26A69A'
        };
      case 'modelNode':
        return {
          label: 'Model',
          icon: faProjectDiagram,
          background: '#CCE5FF',
          color: '#337AB7'
        };
      case 'knowledgeNode':
        return {
          label: 'Knowledgebase',
          icon: faLandmark,
          background: '#E0F7FA',
          color: '#00ACC1'
        };
      case 'toolNode':
        return {
          label: 'Tool',
          icon: '⚒',
          background: '#fff5e6',
          color: '#ff9500'
        };

      case 'apiNode':
        return {
          label: 'API',
          icon: faPlug,
          background: '#E0F7E9',
          color: '#2E7D32'
        };

      case 'emailOutNode':
        return {
          label: 'Email Out',
          icon: faEnvelope,
          background: '#E0F7E9',
          color: '#2E7D32'
        };

      case 'classifierNode':
        return {
          label: 'Classifier',
          icon: faObjectUngroup,
          background: '#D6F5E3',
          color: '#388E3C'
        };

      case 'codeNode':
        return {
          label: 'Code Block',
          icon: faCode,
          background: '#E4FBE9',
          color: '#43A047'
        };

      case 'webHookNode':
        return {
          label: 'Tool',
          icon: faPaperPlane,
          background: '#D2F8D2',
          color: '#4CAF50'
        };
      default:
        return {
          label: 'Node',
          icon: '◆',
          background: '#f0f0f0',
          color: 'var(--text-primary)'
        };
    }
  };



  const nodeTypeInfo = getNodeTypeInfo(selectedNode.type);

  // const handleInputChange = (field, value) => {
  //   updateNodeProperties(selectedNode.id, { [field]: value });
  // };


  const handleInputChange = (field, value) => {
    if (field.startsWith('data.')) {
      const nestedField = field.split('.')[1];
      let tmpData = selectedNode.data.data
      tmpData[nestedField] = value;
      updateNodeProperties(selectedNode.id, { ['data']: tmpData });

    } else {
      // Handle top-level fields
      updateNodeProperties(selectedNode.id, { [field]: value });
    }
  };


  const renderAgentProperties = () => {
    const { agent_code, persona, capabilities = [] } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="agent-type">Agent Type</Label>
          <Select
            id="agent-type"
            value={selectedNode.data.agent_code}
            onChange={(e) => handleInputChange('agent_code', e.target.value)} >
            <option value="">Select Agent</option>
            {agentList.map((agent, index) => (
              <option key={index} value={agent.code}>{agent.name}</option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="agent-persona">Persona</Label>
          <Select
            id="agent-persona"
            value={persona || ''}
            onChange={(e) => handleInputChange('persona', e.target.value)}>
            <option value="">Select Persona</option>
            {personaList.map((persona, index) => (
              <option key={index} value={persona.code}>
                {persona.name}
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Capabilities</Label>
          <TagsContainer>
            {capabilities.map((capability, index) => (
              <Tag key={index}>
                {capability}
                <RemoveTag onClick={() => {
                  const newCapabilities = [...capabilities];
                  newCapabilities.splice(index, 1);
                  handleInputChange('capabilities', newCapabilities);
                }}>✕</RemoveTag>
              </Tag>
            ))}
            <AddTagButton onClick={() => {
              const newCapability = prompt('Enter capability:');
              if (newCapability && !capabilities.includes(newCapability)) {
                handleInputChange('capabilities', [...capabilities, newCapability]);
              }
            }}>+ Add</AddTagButton>
          </TagsContainer>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="knowledge-type">Prompts</Label>
          <Select
            id="knowledge-check_list"
            value={selectedNode.data.check_list}
            onChange={(e) => handleInputChange('check_list', e.target.value)}
          >
            <option value="">Select</option>
            {promptList.map((prompt, index) => (
              <option key={index} value={prompt.code}>{prompt.name}</option>
            ))}
          </Select>
        </FormGroup>
      </>
    );
  };

  const renderModelProperties = () => {
    const { modelType, provider, version } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="model-type">Model Type</Label>
          <Select
            id="model-type"
            value={selectedNode.data.data.model_code}
            onChange={(e) => handleInputChange('data.model_code', e.target.value)} >
            <option value="">Select Model</option>
            {modelList.map((model, index) => (
              <option key={index} value={model.code}>{model.name}</option>
            ))}
          </Select>
        </FormGroup>

        {/* <FormGroup>
          <Label htmlFor="model-version">Version</Label>
          <Input
            id="model-version"
            value={version || ''}
            onChange={(e) => handleInputChange('version', e.target.value)}
            placeholder="e.g. gpt-4, claude-3, etc."
          />
        </FormGroup> */}
      </>
    );
  };

  const renderKnowledgeProperties = () => {
    const { knowledgeType, source } = selectedNode.data;
    console.log(selectedNode.data.data)
    return (
      <>
        <FormGroup>
          <Label htmlFor="knowledge-type">Knowledge Base</Label>
          <Select
            id="knowledge-type"
            value={selectedNode.data.data.kb_code}
            onChange={(e) => handleInputChange('data.kb_code', e.target.value)}
          >
            <option value="">Select</option>
            {knowledgeBaseList.map((kb, index) => (
              <option key={index} value={kb.code}>{kb.name}</option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="knowledge-type">Operation</Label>
          <Select
            id="knowledge-operation"
            value={selectedNode.data.data.operation}
            onChange={(e) => handleInputChange('data.operation', e.target.value)}
          >
            <option value="">Select</option>
            <option value='ingest'>Ingest</option>
            <option value='query'>Query</option>
            <option value='search'>Search</option>
            <option value='report'>Report</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="knowledge-type">Prompts</Label>
          <Select
            id="knowledge-check_list"
            value={selectedNode.data.check_list}
            onChange={(e) => handleInputChange('check_list', e.target.value)}
          >
            <option value="">Select</option>
            {promptList.map((prompt, index) => (
              <option key={index} value={prompt.code}>{prompt.name}</option>
            ))}
          </Select>
        </FormGroup>

        {/* <FormGroup>
          <Label htmlFor="knowledge-source">Source</Label>
          <Input
            id="knowledge-source"
            value={source || ''}
            onChange={(e) => handleInputChange('source', e.target.value)}
            placeholder="e.g. Claims KB, Policy Database, etc."
          />
        </FormGroup> */}
      </>
    );
  };

  const renderToolProperties = () => {
    const { toolType, endpoint, method } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="tool-type">Tool Type</Label>
          <Select
            id="tool-type"
            value={toolType}
            onChange={(e) => handleInputChange('toolType', e.target.value)}
          >
            <option value="api">API</option>
            <option value="code">Code Execution</option>
            <option value="webhook">Webhook</option>
            <option value="function">Function</option>
          </Select>
        </FormGroup>

        {toolType === 'api' || toolType === 'webhook' ? (
          <>
            <FormGroup>
              <Label htmlFor="tool-endpoint">Endpoint</Label>
              <Input
                id="tool-endpoint"
                value={endpoint || ''}
                onChange={(e) => handleInputChange('endpoint', e.target.value)}
                placeholder="e.g. https://api.example.com/v1/data"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="tool-method">Method</Label>
              <Select
                id="tool-method"
                value={method || 'GET'}
                onChange={(e) => handleInputChange('method', e.target.value)}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </Select>
            </FormGroup>
          </>
        ) : toolType === 'code' ? (
          <FormGroup>
            <Label htmlFor="tool-code">
              Code
              <Button type="button" ><FontAwesomeIcon icon={faPlay} /></Button>
            </Label>
            {/* <Textarea
              id="tool-code"
              value={selectedNode.data.data.code_block || ''}
              onChange={(e) => handleInputChange('data.code_block', e.target.value)}
              placeholder="Enter code to execute"
              style={{ minHeight: '120px', fontFamily: 'monospace' }}
            /> */}
            <PythonCodeEditor
              value={selectedNode.data.data.code_block}
              onChange={val => handleInputChange('data.code_block', val)}
            />
          </FormGroup>
        ) : null}
      </>
    );
  };

  const renderApiProperties = () => {
    const { toolType, endpoint, method } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="tool-endpoint">Endpoint</Label>
          <Input
            id="tool-endpoint"
            value={selectedNode.data.data.url || ''}
            onChange={(e) => handleInputChange('data.url', e.target.value)}
            placeholder="e.g. https://api.example.com/v1/data"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="tool-endpoint">Token</Label>
          <Input
            id="tool-token"
            value={selectedNode.data.data.token || ''}
            onChange={(e) => handleInputChange('data.token', e.target.value)}
            placeholder=""
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="tool-method">Method</Label>
          <Select
            id="tool-method"
            value={selectedNode.data.data.method}
            onChange={(e) => handleInputChange('data.method', e.target.value)}
          >
            <option value="">Select</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </Select>
        </FormGroup>

      </>
    );
  };

  const renderEmailOutProperties = () => {
    const { toolType, endpoint, method } = selectedNode.data;


    return (
      <>
        <FormGroup>
          <Label htmlFor="tool-from">From</Label>
          <Input
            id="tool-from"
            value={selectedNode.data.data.frm || ''}
            onChange={(e) => handleInputChange('data.frm', e.target.value)}
            placeholder="e.g. meta-agent@metamorphtech.com"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="tool-to">To</Label>
          <Input
            id="tool-to"
            value={selectedNode.data.data.to || ''}
            onChange={(e) => handleInputChange('data.to', e.target.value)}
            placeholder="e.g. recipient@example.com"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="tool-cc">CC</Label>
          <Input
            id="tool-cc"
            value={selectedNode.data.data.cc || ''}
            onChange={(e) => handleInputChange('data.cc', e.target.value)}
            placeholder="e.g. cc@example.com"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="tool-subject">Subject</Label>
          <Input
            id="tool-subject"
            value={selectedNode.data.data.subject || ''}
            onChange={(e) => handleInputChange('data.subject', e.target.value)}
            placeholder=""
          />
        </FormGroup>



      </>
    );
  };
  const renderCodeProperties = () => {
    const { toolType, endpoint, method } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="tool-code">
            Code
            <Button type="button" onClick={handleOpenEvalModal}><FontAwesomeIcon icon={faPlay} /></Button>
          </Label>
          {/* <Textarea
            id="tool-code"
            value={selectedNode.data.data.code_block || ''}
            onChange={(e) => handleInputChange('data.code_block', e.target.value)}
            placeholder="Enter code to execute"
            style={{ minHeight: '120px', fontFamily: 'monospace' }}
          /> */}
          <PythonCodeEditor
            value={selectedNode.data.data.code_block}
            onChange={val => handleInputChange('data.code_block', val)}
          />
        </FormGroup>

      </>
    );
  };

  const renderClassifierProperties = () => {
    const { toolType, endpoint, method } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="classes_knowledge_base">Classifier</Label>
          <Select
            id="classes_knowledge_base"
            value={selectedNode.data.data.classes_knowledge_base}
            onChange={(e) => handleInputChange('data.classes_knowledge_base', e.target.value)} >
            <option value="">Select Agent</option>
            {knowledgeBaseList.map((kb, index) => (
              <option key={index} value={kb.code}>{kb.name}</option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="classes_query">Class Query</Label>
          {/* <Input
            id="classes_queryt"
            value={selectedNode.data.data.classes_query || ''}
            onChange={(e) => handleInputChange('data.classes_query', e.target.value)}
            placeholder=""
          /> */}
          <Textarea
            id="classes_query"
            value={selectedNode.data.data.classes_query || ''}
            onChange={(e) => handleInputChange('data.classes_query', e.target.value)}
            style={{ minHeight: '120px' }}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="query_suffix">Query SUffix</Label>
          <Textarea
            id="query_suffix"
            value={selectedNode.data.data.query_suffix || ''}
            onChange={(e) => handleInputChange('data.query_suffix', e.target.value)}
            style={{ minHeight: '120px' }}
          />
        </FormGroup>
      </>
    );
  };

  const renderTriggerProperties = () => {
    const { triggerType, schedule } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="trigger-type">Trigger Type</Label>
          <Select
            id="trigger-type"
            value={triggerType}
            onChange={(e) => handleInputChange('triggerType', e.target.value)}
          >
            <option value="manual">Manual</option>
            <option value="scheduled">Scheduled</option>
            <option value="event">Event-based</option>
            <option value="webhook">Webhook</option>
          </Select>
        </FormGroup>

        {triggerType === 'scheduled' && (
          <FormGroup>
            <Label htmlFor="trigger-schedule">Schedule (cron)</Label>
            <Input
              id="trigger-schedule"
              value={schedule || ''}
              onChange={(e) => handleInputChange('schedule', e.target.value)}
              placeholder="e.g. 0 9 * * 1-5"
            />
          </FormGroup>
        )}
      </>
    );
  };

  const renderOutputProperties = () => {
    const { outputType, format, destination } = selectedNode.data;

    return (
      <>
        <FormGroup>
          <Label htmlFor="output-type">Output Type</Label>
          <Select
            id="output-type"
            value={outputType}
            onChange={(e) => handleInputChange('outputType', e.target.value)}
          >
            <option value="response">Response</option>
            <option value="file">File</option>
            <option value="database">Database</option>
            <option value="webhook">Webhook</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="output-format">Format</Label>
          <Select
            id="output-format"
            value={format || 'json'}
            onChange={(e) => handleInputChange('format', e.target.value)}
          >
            <option value="json">JSON</option>
            <option value="xml">XML</option>
            <option value="csv">CSV</option>
            <option value="text">Plain Text</option>
          </Select>
        </FormGroup>

        {outputType !== 'response' && (
          <FormGroup>
            <Label htmlFor="output-destination">Destination</Label>
            <Input
              id="output-destination"
              value={destination || ''}
              onChange={(e) => handleInputChange('destination', e.target.value)}
              placeholder={outputType === 'file' ? 'e.g. /path/to/file.json' :
                outputType === 'database' ? 'e.g. claims_db.results' :
                  'e.g. https://webhook.example.com/callback'}
            />
          </FormGroup>
        )}
      </>
    );
  };

  const renderNodeSpecificProperties = () => {
    switch (selectedNode.type) {
      case 'agentNode':
        return renderAgentProperties();
      case 'modelNode':
        return renderModelProperties();
      case 'knowledgeNode':
        return renderKnowledgeProperties();
      case 'toolNode':
        return renderToolProperties();
      case 'triggerNode':
        return renderTriggerProperties();
      case 'emailOutNode':
        return renderEmailOutProperties();
      case 'apiNode':
        return renderApiProperties();
      case 'codeNode':
        return renderCodeProperties();
      case 'classifierNode':
        return renderClassifierProperties();
      case 'outputNode':
        return renderOutputProperties();
      default:
        return null;
    }
  };

  // Helper to extract workflow_vars from code block (simple regex for demo)
  const extractWorkflowVars = (code) => {
    // Example: look for workflow_vars['varName'] or workflow_vars["varName"]
    const regex = /workflow_vars\[['"](\w+)['"]\]/g;
    const vars = {};
    let match;
    while ((match = regex.exec(code))) {
      vars[match[1]] = '';
    }
    return vars;
  };

  // Fetch last successful run workflow_vars
  const fetchLastRunVars = async () => {
    const nodeCode = selectedNode.id;
    if (!nodeCode) return;
    const filters = {
      transaction_status: 'CLOSED',
      code: nodeCode
    };
    const sort = {"value":"desc"};
    const results = await getNodesForPipeline(filters, sort);

    return results;

    // if (results && results.length > 0) {
    //   const node = results[0];
    //   setLastRunVars(node.workflow_vars || {});
    //   setWorkflowVars(node.workflow_vars || {});
    // }
  };

  // Open modal and initialize workflow vars
  const handleOpenEvalModal = async () => {
    setShowEvalModal(true);
    setWorkflowVarsMode('manual');
    setEvalResult(null);
    setLastRunVars(null);
    // Extract workflow vars from code
    const code = selectedNode?.data?.data?.code_block || '';
    setWorkflowVars(extractWorkflowVars(code));
  };

  // Handle mode change
  const handleWorkflowVarsModeChange = async (mode) => {
    setWorkflowVarsMode(mode);
    if (mode === 'lastRun') {
      const resp = await fetchLastRunVars();
      if (resp.length) {
        // setWorkflowVars(res[0].workflow_vars)
        console.log(resp[0]?.workflow_vars)
        const result = extractWorkflowVarsForLastRun(resp[0]?.workflow_vars);
        setWorkflowVars(result)
      }
    } else {
      // Reset to extracted
      const code = selectedNode?.data?.data?.code_block || '';
      setWorkflowVars(extractWorkflowVars(code));
      console.log(code)
      console.log(extractWorkflowVars(code))
    }
  };

  const extractWorkflowVarsForLastRun = (obj) => {
    const result = {};

    for (const key in obj) {
      const value = obj[key];

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        result[key] = JSON.stringify(value);
      } else {
        result[key] = value;
      }
    }

    return result;
  }


  // Handle workflow var change
  const handleWorkflowVarChange = (key, value) => {
    setWorkflowVars((prev) => ({ ...prev, [key]: value }));
  };

  // Evaluate code block
  const handleEvaluate = async () => {
    setIsEvaluating(true);
    setEvalResult(null);
    try {
      const codeBlock = selectedNode?.data?.data?.code_block || '';
      const payload = {
        code_block: [codeBlock],
        request: {},
        workflow_vars: workflowVars,
        verify_bool: false,
      };
      const res = await evaluateNodeExpression(payload);
      setEvalResult(res.data || res);
    } catch (err) {
      setEvalResult({ error: err.message });
    } finally {
      setIsEvaluating(false);
    }
  };

  return (
    <PanelContainer>
      <PanelTitle>Properties</PanelTitle>
      <div style={{ paddingBottom: '1rem', height: 'calc(100dvh - 228px)', overflowY: 'auto' }}>
        <NodeHeader>
          <NodeTypeIcon
            background={nodeTypeInfo.background}
            color={nodeTypeInfo.color}
          >
            <FontAwesomeIcon icon={nodeTypeInfo.icon} />
          </NodeTypeIcon>
          <NodeInfo>
            {/* <NodeType>{nodeTypeInfo.label}</NodeType> */}
            <NodeName>{nodeTypeInfo.label}</NodeName>
            {/* <NodeName>{selectedNode.data.label}</NodeName> */}
          </NodeInfo>
        </NodeHeader>

        <SectionTitle>Basic Properties</SectionTitle>

        <FormGroup>
          <Label htmlFor="node-label">Label</Label>
          <Input
            id="node-label"
            value={selectedNode.data.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="node-description">Description</Label>
          <Textarea
            id="node-description"
            value={selectedNode.data.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
          />
        </FormGroup>

        <SectionTitle>Specific Properties</SectionTitle>

        {renderNodeSpecificProperties()}
      </div>
      {/* <PipelineSection>
        <SectionTitle>Pipeline Properties</SectionTitle>

        <FormGroup>
          <Label htmlFor="pipeline-description">Description</Label>
          <Textarea
            id="pipeline-description"
            value={pipelineDescription}
            onChange={(e) => setPipelineDescription(e.target.value)}
            placeholder="Describe the purpose of this pipeline"
          />
        </FormGroup>
      </PipelineSection> */}
      {/* EVALUATION MODAL */}
      {showEvalModal && (
        <>
          <Modal isOpen={showEvalModal} onClose={() => setShowEvalModal(false)} size="large">

            <div className='evalModal' style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Section 1: Workflow Variables */}
              <div>
                <h3>Workflow Variables</h3>
                <div style={{ marginBottom: 8 }}>
                  <label>
                    <input
                      type="radio"
                      checked={workflowVarsMode === 'manual'}
                      onChange={() => handleWorkflowVarsModeChange('manual')}
                    />{' '}
                    Manual
                  </label>
                  <label style={{ marginLeft: 16 }}>
                    <input
                      type="radio"
                      checked={workflowVarsMode === 'lastRun'}
                      onChange={() => handleWorkflowVarsModeChange('lastRun')}
                    />{' '}
                    Use Last Successful Run
                  </label>
                </div>
                <div>
                  {Object.keys(workflowVars).length === 0 && <div style={{ color: '#888' }}>No workflow variables found.</div>}
                  {Object.keys(workflowVars).map((key) => (
                    <div key={key} style={{ marginBottom: 8 }}>
                      <label style={{ marginRight: 8 }}>{key}:</label>
                      {key !== 'ingestion_request' ? <Textarea
                        style={{ width: '100%', fontFamily: 'monospace' }}
                        type="text"
                        value={workflowVars[key]}
                        onChange={e => handleWorkflowVarChange(key, e.target.value)}
                        disabled={workflowVarsMode === 'lastRun'}
                      /> : <pre style={{
                        background: '#f6f6f6',
                        padding: 12,
                        borderRadius: 4,
                        minHeight: 80,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {workflowVars[key] ? JSON.stringify(JSON.parse(workflowVars[key]), null, 2) : 'No result yet.'}
                      </pre>}
                    </div>
                  ))}
                </div>
              </div>
              {/* Section 2: Code Block */}
              <div>
                <h3>Code Block</h3>
                <PythonCodeEditor
                  value={selectedNode?.data?.data?.code_block || ''}
                  onChange={() => { }}
                  readOnly
                />
                <Button onClick={handleEvaluate} disabled={isEvaluating} style={{ marginTop: 12 }}>
                  {isEvaluating ? 'Evaluating...' : 'Play'}
                </Button>
              </div>
              {/* Section 3: Result */}
              <div>
                <h3>Result</h3>
                <pre style={{
                  background: '#f6f6f6',
                  padding: 12,
                  borderRadius: 4,
                  minHeight: 80,
                  whiteSpace: 'pre-wrap',       
                  wordBreak: 'break-word',      
                  overflowWrap: 'break-word'    
                }}>
                  {evalResult ? JSON.stringify(evalResult, null, 2) : 'No result yet.'}
                </pre>
              </div>
            </div>
          </Modal>
        </>
      )}
    </PanelContainer>
  );
};

export default PropertiesPanel;
